import React, { useState, useEffect, useRef } from 'react';

/**
 * Composant de sélection d'opportunité avec recherche rapide
 */
const OpportunitySelect = ({ 
  value, 
  onChange, 
  opportunities = [],
  placeholder = "Rechercher une opportunité...",
  className = "",
  required = false,
  disabled = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Fermer le dropdown quand on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filtrer les opportunités en fonction de la recherche
  const filteredOpportunities = opportunities.filter(opp => 
    opp.numero_opportunite?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    opp.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    opp.nom_opportunite?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Gérer la sélection
  const handleSelect = (opportunity) => {
    onChange(opportunity.id);
    setSearchTerm('');
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  // Navigation au clavier
  const handleKeyDown = (e) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setIsOpen(true);
      e.preventDefault();
      return;
    }

    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOpportunities.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOpportunities.length) {
          handleSelect(filteredOpportunities[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Afficher la valeur sélectionnée
  const selectedOpp = opportunities.find(opp => opp.id === value);
  const displayValue = selectedOpp 
    ? `${selectedOpp.numero_opportunite} - ${selectedOpp.client_name}`
    : '';

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? searchTerm : displayValue}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
            setHighlightedIndex(-1);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className="w-full border border-gray-300 rounded-md px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {isOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            )}
          </svg>
        </div>
      </div>

      {/* Dropdown avec résultats */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-y-auto">
          {filteredOpportunities.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">
              {searchTerm ? `Aucune opportunité trouvée pour "${searchTerm}"` : 'Aucune opportunité disponible'}
            </div>
          ) : (
            <div>
              {filteredOpportunities.map((opp, index) => (
                <button
                  key={opp.id}
                  type="button"
                  onClick={() => handleSelect(opp)}
                  className={`w-full text-left px-4 py-3 text-sm hover:bg-emerald-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                    index === highlightedIndex ? 'bg-emerald-100' : ''
                  } ${value === opp.id ? 'bg-emerald-50 font-medium' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">{opp.numero_opportunite}</div>
                      <div className="text-xs text-gray-500 mt-1">{opp.client_name}</div>
                      {opp.nom_opportunite && (
                        <div className="text-xs text-emerald-600 mt-1">{opp.nom_opportunite}</div>
                      )}
                    </div>
                    {value === opp.id && (
                      <span className="text-emerald-600">✓</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Aide visuelle */}
      {isOpen && filteredOpportunities.length > 0 && (
        <div className="mt-1 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-md text-xs text-emerald-700">
          💡 Recherche par numéro, client ou nom • ↑↓ pour naviguer • Entrée pour sélectionner
        </div>
      )}
    </div>
  );
};

export default OpportunitySelect;
